import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PartFile, ValidationResult } from '../../quote-assist-v2.model';
import { FileUploadEvent, FileUploadProgressEvent, FileValidationEvent } from '../../models/component-events.model';

@Component({
  selector: 'app-file-upload-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './file-upload-modal.component.html',
  styleUrls: ['./file-upload-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadModalComponent implements OnInit, OnDestroy {
  @Input() partIndex?: number;
  @Input() contactId?: number; // Added for future functionality like PDF downloads
  @Output() fileUpload = new EventEmitter<FileUploadEvent>();
  @Output() close = new EventEmitter<void>();

  // File Upload State
  isDragOver = false;
  isUploading = false;
  uploadProgress = 0;
  selectedFiles: File[] = [];
  uploadedFiles: PartFile[] = [];
  validationErrors: { [fileName: string]: string[] } = {};
  uploadErrors: { [fileName: string]: string } = {};

  // File Validation
  readonly maxFileSize = 10 * 1024 * 1024; // 10MB
  readonly allowedFileTypes = [
    // 2D Drawing Files
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff',
    'image/tif',
    // CAD files (these might have different MIME types depending on the system)
    'application/acad',
    'application/dwg',
    'application/dxf',
    'application/octet-stream', // For .step, .stp, .igs, .iges, .ipt, .x_t, .stl, .obj, .3mf files
    // Office files
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'application/zip',
    'application/x-zip-compressed'
  ];

  // UI State
  showHelp = false;
  currentTab: 'upload' | 'uploaded' = 'upload';

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.clearUploadState();
  }

  // ===== INITIALIZATION =====

  private initializeComponent(): void {
    this.resetState();
  }

  private resetState(): void {
    this.selectedFiles = [];
    this.uploadedFiles = [];
    this.validationErrors = {};
    this.uploadErrors = {};
    this.uploadProgress = 0;
    this.isUploading = false;
    this.currentTab = 'upload';
  }

  // ===== FILE SELECTION =====

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      this.processSelectedFiles(files);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files) {
      const files = Array.from(event.dataTransfer.files);
      this.processSelectedFiles(files);
    }
  }

  private processSelectedFiles(files: File[]): void {
    const validFiles: File[] = [];
    const errors: { [fileName: string]: string[] } = {};

    files.forEach(file => {
      const validation = this.validateFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        errors[file.name] = [validation.message || 'Invalid file'];
      }
    });

    this.selectedFiles = [...this.selectedFiles, ...validFiles];
    this.validationErrors = { ...this.validationErrors, ...errors };

    if (validFiles.length > 0) {
      this.logEvent('Files Selected', { 
        count: validFiles.length, 
        fileNames: validFiles.map(f => f.name) 
      });
    }
  }

  // ===== FILE VALIDATION =====

  private validateFile(file: File): ValidationResult {
    const errors: string[] = [];

    // Check file size
    if (file.size > this.maxFileSize) {
      errors.push(`File size exceeds ${this.formatFileSize(this.maxFileSize)}`);
    }

    // Check file type (both MIME type and file extension)
    const isValidType = this.isValidFileType(file);
    if (!isValidType) {
      errors.push('File type not supported');
    }

    // Check if file already exists
    const existingFile = this.selectedFiles.find(f => f.name === file.name);
    if (existingFile) {
      errors.push('File already selected');
    }

    return {
      isValid: errors.length === 0,
      message: errors.join(', '),
      fieldName: file.name
    };
  }

  private isValidFileType(file: File): boolean {
    // Check MIME type first
    if (this.allowedFileTypes.includes(file.type)) {
      return true;
    }

    // If MIME type is not recognized (common with CAD files), check file extension
    const fileName = file.name.toLowerCase();
    const allowedExtensions = [
      // 2D Drawing Files
      '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tif', '.tiff',
      // CAD Files
      '.dwg', '.dxf', '.step', '.stp', '.sldprt', '.slddrw', '.igs', '.iges', '.ipt', '.x_t', '.stl', '.obj', '.3mf',
      // Office Files
      '.doc', '.docx', '.xls', '.xlsx', '.txt', '.zip'
    ];

    return allowedExtensions.some(ext => fileName.endsWith(ext));
  }

  public formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  public getObjectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  // ===== FILE UPLOAD =====

  async uploadFiles(): Promise<void> {
    if (this.selectedFiles.length === 0) {
      this.emitError('No files selected for upload');
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadErrors = {};

    try {
      const uploadedFiles: PartFile[] = [];
      const totalFiles = this.selectedFiles.length;

      for (let i = 0; i < this.selectedFiles.length; i++) {
        const file = this.selectedFiles[i];
        
        try {
          const uploadedFile = await this.uploadSingleFile(file, i, totalFiles);
          uploadedFiles.push(uploadedFile);
          
          // Update progress
          this.uploadProgress = ((i + 1) / totalFiles) * 100;
          
        } catch (error) {
          this.uploadErrors[file.name] = error instanceof Error ? error.message : 'Upload failed';
          this.logEvent('File Upload Error', { fileName: file.name, error });
        }
      }

      // Update uploaded files list
      this.uploadedFiles = [...this.uploadedFiles, ...uploadedFiles];
      
      // Emit success event
      const success = uploadedFiles.length === this.selectedFiles.length;
      this.fileUpload.emit({
        files: uploadedFiles,
        partIndex: this.partIndex,
        success,
        error: success ? undefined : 'Some files failed to upload'
      });

      if (success) {
        this.logEvent('Files Uploaded Successfully', { 
          count: uploadedFiles.length,
          fileNames: uploadedFiles.map(f => f.fileName)
        });
        this.resetState();
      }

    } catch (error) {
      this.emitError('Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      this.isUploading = false;
    }
  }

  private async uploadSingleFile(file: File, index: number, total: number): Promise<PartFile> {
    return new Promise((resolve, reject) => {
      // Simulate file upload with progress
      const reader = new FileReader();
      
      reader.onload = () => {
        // Simulate network delay
        setTimeout(() => {
          const uploadedFile: PartFile = {
            fileName: file.name,
            fileType: file.type,
            fileURL: reader.result as string // In real app, this would be the uploaded URL
          };
          
          resolve(uploadedFile);
        }, Math.random() * 1000 + 500); // Random delay between 500-1500ms
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  // ===== FILE MANAGEMENT =====

  removeSelectedFile(fileName: string): void {
    this.selectedFiles = this.selectedFiles.filter(f => f.name !== fileName);
    delete this.validationErrors[fileName];
    
    this.logEvent('File Removed', { fileName });
  }

  removeUploadedFile(fileName: string): void {
    this.uploadedFiles = this.uploadedFiles.filter(f => f.fileName !== fileName);
    
    this.logEvent('Uploaded File Removed', { fileName });
  }

  clearAllFiles(): void {
    this.selectedFiles = [];
    this.uploadedFiles = [];
    this.validationErrors = {};
    this.uploadErrors = {};
    
    this.logEvent('All Files Cleared');
  }

  // ===== UI HELPERS =====

  getFileIcon(fileType: string): string {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('word')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('zip') || fileType.includes('compressed')) return '📦';
    // CAD file icons
    if (fileType.includes('dwg') || fileType.includes('dxf')) return '📐';
    if (fileType.includes('step') || fileType.includes('stp') || fileType.includes('igs') || fileType.includes('iges')) return '🔧';
    if (fileType.includes('stl') || fileType.includes('obj') || fileType.includes('3mf')) return '🎨';
    if (fileType.includes('sldprt') || fileType.includes('slddrw') || fileType.includes('ipt')) return '⚙️';
    if (fileType.includes('x_t')) return '🔩';
    return '📎';
  }

  getFileTypeLabel(fileType: string): string {
    if (fileType.includes('pdf')) return 'PDF Document';
    if (fileType.includes('jpeg') || fileType.includes('jpg')) return 'JPEG Image';
    if (fileType.includes('png')) return 'PNG Image';
    if (fileType.includes('gif')) return 'GIF Image';
    if (fileType.includes('bmp')) return 'BMP Image';
    if (fileType.includes('tif') || fileType.includes('tiff')) return 'TIFF Image';
    if (fileType.includes('word')) return 'Word Document';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'Excel Spreadsheet';
    if (fileType.includes('zip') || fileType.includes('compressed')) return 'Compressed File';
    if (fileType.includes('text')) return 'Text File';
    // CAD file labels
    if (fileType.includes('dwg')) return 'AutoCAD Drawing';
    if (fileType.includes('dxf')) return 'AutoCAD DXF';
    if (fileType.includes('step') || fileType.includes('stp')) return 'STEP File';
    if (fileType.includes('igs') || fileType.includes('iges')) return 'IGES File';
    if (fileType.includes('stl')) return 'STL File';
    if (fileType.includes('obj')) return 'OBJ File';
    if (fileType.includes('3mf')) return '3MF File';
    if (fileType.includes('sldprt')) return 'SolidWorks Part';
    if (fileType.includes('slddrw')) return 'SolidWorks Drawing';
    if (fileType.includes('ipt')) return 'Inventor Part';
    if (fileType.includes('x_t')) return 'Parasolid File';
    return 'Document';
  }

  // ===== EVENT HANDLERS =====

  onClose(): void {
    if (this.isUploading) {
      // Don't allow closing during upload
      return;
    }
    
    this.close.emit();
  }

  onTabChange(tab: 'upload' | 'uploaded'): void {
    this.currentTab = tab;
  }

  toggleHelp(): void {
    this.showHelp = !this.showHelp;
  }

  // ===== UTILITY METHODS =====

  trackByFileName(index: number, file: File | PartFile): string {
    return 'fileName' in file ? file.fileName : file.name;
  }

  downloadFile(file: PartFile): void {
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = file.fileURL;
    link.download = file.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    this.logEvent('File Downloaded', { fileName: file.fileName });
  }

  private emitError(message: string): void {
    this.fileUpload.emit({
      files: [],
      partIndex: this.partIndex,
      success: false,
      error: message
    });
  }

  private clearUploadState(): void {
    this.selectedFiles = [];
    this.uploadedFiles = [];
    this.validationErrors = {};
    this.uploadErrors = {};
  }

  private logEvent(eventName: string, data?: any): void {
    console.log(`[FileUploadModal] ${eventName}`, data);
  }

  // ===== GETTERS =====

  get hasSelectedFiles(): boolean {
    return this.selectedFiles.length > 0;
  }

  get hasUploadedFiles(): boolean {
    return this.uploadedFiles.length > 0;
  }

  get canUpload(): boolean {
    return this.hasSelectedFiles && !this.isUploading;
  }

  get hasErrors(): boolean {
    return Object.keys(this.validationErrors).length > 0 || 
           Object.keys(this.uploadErrors).length > 0;
  }

  get totalSelectedSize(): number {
    return this.selectedFiles.reduce((total, file) => total + file.size, 0);
  }

  get formattedTotalSize(): string {
    return this.formatFileSize(this.totalSelectedSize);
  }
} 